import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { login as apiLogin, forgotPassword } from "../api/authService";
import { useAuth } from "../contexts/AuthContext";
import { getPosition, isPermissionReason, locationErrorMessage } from "../utils/geolocation";
import { showToast, showApiError } from "../utils/toast";
import { homePathForRole } from "../utils/roleHome";

// Extra line shown under the error when the block is a permission the user
// has to clear themselves. The installed PWA has no address bar, so the old
// "allow it for this site" advice pointed at a control that isn't there.
const UNBLOCK_HINT =
  "If you installed the app to your home screen, allow Location in your device settings " +
  "(Android: Settings › Apps › SMART CA › Permissions; iOS: Settings › SMART CA) — " +
  "the in-app prompt won't appear again.";

const FEATURES = [
  { icon: "sliders", label: "SMART CA OFFICE WORK-FLOW MANAGEMENT" },
  { icon: "users", label: "REAL TIME CLIENT HANDLING" },
  { icon: "lock", label: "ROLE BASED ACCESS" },
  { icon: "calendar", label: "TO-DO'S AND CALENDAR MANAGEMENT" },
  { icon: "check-square", label: "CUSTOMISED STAFF TASK BOARD" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // Self-service reset: clients can't get a secret out-of-band, so this just
  // asks the firm's admins (in-app) to reset — no password is delivered here.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setErrors({});
    if (!username.trim() || !password) {
      setErrors({ form: "Enter your username and password" });
      return;
    }
    setLoading(true);
    try {
      // Two-phase geofence flow: try without coordinates first, so only fenced
      // employees are ever asked for location. If the server says a location is
      // required, fetch it and retry once.
      let res;
      try {
        res = await apiLogin(username.trim(), password);
      } catch (err) {
        if (err?.data?.code !== "LOCATION_REQUIRED") throw err;
        // This is the one place a fenced employee is asked for location by a
        // real user gesture, so it's worth waking the GPS for a precise fix.
        const fix = await getPosition({ highAccuracy: true, timeout: 12000, maximumAge: 60000 });
        if (!fix.ok) {
          // Say which of the three failures happened. A timeout is not a
          // denial, and telling someone who granted access to grant access
          // leaves them with nothing to do.
          const msg = locationErrorMessage(fix.reason);
          setErrors({
            form: msg,
            hint: isPermissionReason(fix.reason) ? UNBLOCK_HINT : null,
          });
          showApiError({ message: msg });
          return;
        }
        res = await apiLogin(username.trim(), password, {
          latitude: fix.coords.latitude,
          longitude: fix.coords.longitude,
        });
      }
      const { user, token } = res.data;
      login(user, token);
      showToast("success", res.message || "Logged in");
      // The Authenticated guard redirects to /change-password when the user
      // is still on a temporary password, so we always land them home here.
      navigate(homePathForRole(user.role), { replace: true });
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err, "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const openForgot = () => {
    setForgotUsername(username);
    setForgotSent(false);
    setForgotOpen(true);
  };

  const handleForgot = async (e) => {
    e?.preventDefault();
    const u = forgotUsername.trim();
    if (!u) return;
    setForgotSending(true);
    try {
      await forgotPassword(u);
      // Always generic — the API never reveals whether the account exists.
      setForgotSent(true);
    } catch (err) {
      showApiError(err, "Could not submit the request");
    } finally {
      setForgotSending(false);
    }
  };

  return (
    <div className="login-page">
      {/* === Hero === */}
      <aside className="login-hero">
        <div className="login-hero-bg" aria-hidden>
          <div className="login-hero-orb login-hero-orb--1" />
          <div className="login-hero-orb login-hero-orb--2" />
          <div className="login-hero-orb login-hero-orb--3" />
          <div className="login-hero-grid" />
        </div>

        <div className="login-hero-top">
          <div className="login-hero-brand">
            <img src="/logo.svg" alt="Devsoleil" className="login-hero-logo" />
            <div>
              <div className="login-hero-brand-title">Smart CA</div>
              <div className="login-hero-brand-sub">PRACTICE CONSOLE</div>
            </div>
          </div>
          <div className="login-hero-tricolor" aria-hidden>
            <span /><span /><span />
          </div>
        </div>

        <div className="login-hero-body">
          <div className="login-hero-eyebrow">
            <Icon name="zap" size={11} /> Built for Indian CA's
          </div>
          <h1 className="login-hero-title">
            Run your practice <em>like clockwork.</em>
          </h1>
          <p className="login-hero-tagline">
            Compliance calendars, recurring tasks, client credentials, and a customer portal — in one quiet console that
            works in the background while you do the work that matters.
          </p>

          <div className="login-hero-features">
            {FEATURES.map((f) => (
              <div key={f.label} className="login-hero-feature">
                <div className="login-hero-feature-icon">
                  <Icon name={f.icon} size={14} />
                </div>
                <div className="login-hero-feature-title">{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="login-hero-foot">
          © {new Date().getFullYear()} Smart CA · Made for Indian CA's
        </div>
      </aside>

      {/* === Form === */}
      <main className="login-form-side">
        <div className="login-card">
          {/* Mobile-only inline brand (hero is hidden on small screens) */}
          <div className="login-mobile-brand">
            <img src="/logo.svg" alt="Devsoleil" />
            <div>
              <div className="login-mobile-brand-title">Smart CA</div>
              <div className="login-mobile-brand-sub">PRACTICE CONSOLE</div>
            </div>
          </div>

          <form onSubmit={handleLogin} className="login-form">
              <div className="login-form-head">
                <h2>Welcome back</h2>
                <p>Sign in with the username and password your admin gave you.</p>
              </div>

              <div className="field">
                <label className="field-label">Username</label>
                <input
                  className="input"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="yourusername"
                  autoFocus
                />
                {errors.username && <div className="field-error">{errors.username}</div>}
              </div>

              <div className="field">
                <label className="field-label">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    className="input"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="login-link"
                    onClick={() => setShowPassword((s) => !s)}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)" }}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <Icon name={showPassword ? "eye-off" : "eye"} size={14} />
                  </button>
                </div>
                {errors.password && <div className="field-error">{errors.password}</div>}
              </div>

              {errors.form && <div className="field-error">{errors.form}</div>}
              {errors.hint && (
                <div className="field-hint" style={{ marginTop: 4 }}>{errors.hint}</div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-lg login-submit"
                disabled={loading || !username.trim() || !password}
              >
                {loading ? (
                  <>
                    <span className="spinner spinner-sm" /> Signing in…
                  </>
                ) : (
                  <>
                    Sign in <Icon name="arrow-right" size={14} />
                  </>
                )}
              </button>

              <div className="login-meta">
                <Icon name="lock" size={11} /> Sessions auto-expire after 30 days.{" "}
                <button type="button" className="login-link" onClick={openForgot}>
                  Forgot your password?
                </button>
              </div>
            </form>

            {forgotOpen && (
              forgotSent ? (
                <div className="login-meta" style={{ marginTop: 12 }}>
                  <Icon name="check" size={11} /> If that account exists, your firm has been notified to reset your
                  password — they'll share the new one with you.{" "}
                  <button type="button" className="login-link" onClick={() => setForgotOpen(false)}>
                    Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} style={{ marginTop: 12 }}>
                  <div className="field">
                    <label className="field-label">Your username</label>
                    <input
                      className="input"
                      value={forgotUsername}
                      onChange={(e) => setForgotUsername(e.target.value)}
                      placeholder="yourusername"
                      autoComplete="username"
                      autoFocus
                    />
                  </div>
                  <div className="row gap-sm" style={{ marginTop: 8 }}>
                    <button type="submit" className="btn btn-primary" disabled={forgotSending || !forgotUsername.trim()}>
                      {forgotSending ? "Sending…" : "Notify my firm"}
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setForgotOpen(false)} disabled={forgotSending}>
                      Cancel
                    </button>
                  </div>
                  <div className="login-meta" style={{ marginTop: 8 }}>
                    Your firm's admin will reset your password and share the new one with you.
                  </div>
                </form>
              )
            )}
        </div>

        <div className="login-form-foot">
          Trouble signing in? Contact your firm's admin.
        </div>
      </main>
    </div>
  );
}
